<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const state = reactive({
  enabled: false,
  fromName: '',
  fromEmail: '',
  address: '',
  port: '',
  login: '',
  password: '',
  domain: '',
  encryption: 'starttls',
  verifyMode: 'none',
  authentication: 'login',
});

const touched = reactive({
  fromEmail: false,
  address: false,
  port: false,
  login: false,
  password: false,
  domain: false,
});

const savedEnabled = ref(false);
const passwordConfigured = ref(false);
const saveError = ref('');

const authenticationOptions = computed(() => [
  { value: 'plain', label: t('INBOX_MGMT.WEB_WIDGET_SMTP.AUTH.PLAIN') },
  { value: 'login', label: t('INBOX_MGMT.WEB_WIDGET_SMTP.AUTH.LOGIN') },
  { value: 'cram_md5', label: t('INBOX_MGMT.WEB_WIDGET_SMTP.AUTH.CRAM_MD5') },
]);

const verifyModeOptions = computed(() => [
  { value: 'none', label: t('INBOX_MGMT.WEB_WIDGET_SMTP.VERIFY.NONE') },
  { value: 'peer', label: t('INBOX_MGMT.WEB_WIDGET_SMTP.VERIFY.PEER') },
  {
    value: 'client_once',
    label: t('INBOX_MGMT.WEB_WIDGET_SMTP.VERIFY.CLIENT_ONCE'),
  },
  {
    value: 'fail_if_no_peer_cert',
    label: t('INBOX_MGMT.WEB_WIDGET_SMTP.VERIFY.FAIL_IF_NO_PEER_CERT'),
  },
]);

const requiredValue = value => Boolean(String(value ?? '').trim());
const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const validationErrors = computed(() => {
  if (!state.enabled) return {};

  const errors = {};
  if (!requiredValue(state.fromEmail)) {
    errors.fromEmail = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.REQUIRED');
  } else if (!validEmail(state.fromEmail)) {
    errors.fromEmail = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.EMAIL');
  }
  ['address', 'login', 'domain'].forEach(field => {
    if (!requiredValue(state[field])) {
      errors[field] = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.REQUIRED');
    }
  });
  const port = Number(state.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    errors.port = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.PORT');
  }
  if (!passwordConfigured.value && !requiredValue(state.password)) {
    errors.password = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.REQUIRED');
  }
  return errors;
});

const isUpdating = computed(() => uiFlags.value.isUpdatingSMTP);
const isSavedReady = computed(
  () => savedEnabled.value && passwordConfigured.value
);
const showReadinessWarning = computed(
  () => props.inbox.continuity_via_email && !isSavedReady.value
);

const inputMessage = field => {
  if (touched[field] && validationErrors.value[field]) {
    return validationErrors.value[field];
  }
  if (field === 'password' && passwordConfigured.value) {
    return t('INBOX_MGMT.WEB_WIDGET_SMTP.PASSWORD.KEEP_HINT');
  }
  if (field === 'port') return t('INBOX_MGMT.WEB_WIDGET_SMTP.PORT.HINT');
  return '';
};

const inputMessageType = field =>
  touched[field] && validationErrors.value[field] ? 'error' : 'info';

const touch = field => {
  touched[field] = true;
};

const resetTouched = () => {
  Object.keys(touched).forEach(field => {
    touched[field] = false;
  });
};

const syncFromInbox = inbox => {
  state.enabled = Boolean(inbox.smtp_enabled);
  state.fromName = inbox.smtp_from_name || '';
  state.fromEmail = inbox.smtp_from_email || '';
  state.address = inbox.smtp_address || '';
  state.port = inbox.smtp_port || '';
  state.login = inbox.smtp_login || '';
  state.password = '';
  state.domain = inbox.smtp_domain || '';
  state.encryption = inbox.smtp_enable_ssl_tls ? 'ssl_tls' : 'starttls';
  state.verifyMode = inbox.smtp_openssl_verify_mode || 'none';
  state.authentication = inbox.smtp_authentication || 'login';
  savedEnabled.value = Boolean(inbox.smtp_enabled);
  passwordConfigured.value = Boolean(inbox.smtp_password_configured);
  saveError.value = '';
  resetTouched();
};

watch(
  () => props.inbox.id,
  () => syncFromInbox(props.inbox),
  { immediate: true }
);

const touchRequiredFields = () => {
  Object.keys(touched).forEach(field => {
    touched[field] = true;
  });
};

const updateInbox = async () => {
  saveError.value = '';
  touchRequiredFields();
  if (Object.keys(validationErrors.value).length) {
    saveError.value = t('INBOX_MGMT.WEB_WIDGET_SMTP.VALIDATION.CHECK_FIELDS');
    return;
  }

  const channel = {
    smtp_enabled: state.enabled,
    smtp_from_name: state.fromName,
    smtp_from_email: state.fromEmail,
    smtp_address: state.address,
    smtp_port: state.port,
    smtp_login: state.login,
    smtp_domain: state.domain,
    smtp_enable_starttls_auto: state.encryption === 'starttls',
    smtp_enable_ssl_tls: state.encryption === 'ssl_tls',
    smtp_openssl_verify_mode: state.verifyMode,
    smtp_authentication: state.authentication,
  };

  if (requiredValue(state.password)) channel.smtp_password = state.password;

  try {
    await store.dispatch('inboxes/updateInboxSMTP', {
      id: props.inbox.id,
      channel,
    });
    syncFromInbox(store.getters['inboxes/getInbox'](props.inbox.id));
    useAlert(t('INBOX_MGMT.WEB_WIDGET_SMTP.SUCCESS'));
  } catch (error) {
    saveError.value = error.message || t('INBOX_MGMT.WEB_WIDGET_SMTP.ERROR');
  }
};
</script>

<template>
  <SettingsFieldSection
    :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.TITLE')"
    :help-text="t('INBOX_MGMT.WEB_WIDGET_SMTP.SUBTITLE')"
    class="mt-6 [&>div]:!items-start"
  >
    <form class="space-y-5" @submit.prevent="updateInbox">
      <p
        v-if="saveError"
        role="alert"
        class="rounded-xl bg-n-ruby-3 px-3 py-2.5 text-sm text-n-ruby-11"
      >
        {{ saveError }}
      </p>
      <div
        v-if="showReadinessWarning"
        class="rounded-xl bg-n-amber-3 px-3 py-2.5 text-sm text-n-amber-11 outline outline-1 -outline-offset-1 outline-n-amber-5"
      >
        {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.READINESS_WARNING') }}
      </div>
      <div
        v-else-if="isSavedReady"
        class="rounded-xl bg-n-teal-3 px-3 py-2.5 text-sm text-n-teal-11 outline outline-1 -outline-offset-1 outline-n-teal-5"
      >
        {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.READY') }}
      </div>

      <div class="flex items-start justify-between gap-4">
        <div>
          <p class="mb-1 text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.ENABLE') }}
          </p>
          <p class="mb-0 text-label-small text-n-slate-11">
            {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.ENABLE_HELP') }}
          </p>
        </div>
        <ToggleSwitch v-model="state.enabled" />
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="state.fromName"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.FROM_NAME.LABEL')"
            :placeholder="t('INBOX_MGMT.WEB_WIDGET_SMTP.FROM_NAME.PLACEHOLDER')"
          />
          <Input
            v-model="state.fromEmail"
            type="email"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.FROM_EMAIL.LABEL')"
            :placeholder="
              t('INBOX_MGMT.WEB_WIDGET_SMTP.FROM_EMAIL.PLACEHOLDER')
            "
            :message="inputMessage('fromEmail')"
            :message-type="inputMessageType('fromEmail')"
            @blur="touch('fromEmail')"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            v-model="state.address"
            class="sm:col-span-2"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.ADDRESS.LABEL')"
            :placeholder="t('INBOX_MGMT.WEB_WIDGET_SMTP.ADDRESS.PLACEHOLDER')"
            :message="inputMessage('address')"
            :message-type="inputMessageType('address')"
            @blur="touch('address')"
          />
          <Input
            v-model="state.port"
            type="number"
            min="1"
            max="65535"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.PORT.LABEL')"
            :placeholder="t('INBOX_MGMT.WEB_WIDGET_SMTP.PORT.PLACEHOLDER')"
            :message="inputMessage('port')"
            :message-type="inputMessageType('port')"
            @blur="touch('port')"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="state.login"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.LOGIN.LABEL')"
            :placeholder="t('INBOX_MGMT.WEB_WIDGET_SMTP.LOGIN.PLACEHOLDER')"
            :message="inputMessage('login')"
            :message-type="inputMessageType('login')"
            @blur="touch('login')"
          />
          <Input
            v-model="state.password"
            type="password"
            :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.PASSWORD.LABEL')"
            :placeholder="
              passwordConfigured
                ? t(
                    'INBOX_MGMT.WEB_WIDGET_SMTP.PASSWORD.CONFIGURED_PLACEHOLDER'
                  )
                : t('INBOX_MGMT.WEB_WIDGET_SMTP.PASSWORD.PLACEHOLDER')
            "
            :message="inputMessage('password')"
            :message-type="inputMessageType('password')"
            @blur="touch('password')"
          />
        </div>

        <Input
          v-model="state.domain"
          :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.DOMAIN.LABEL')"
          :placeholder="t('INBOX_MGMT.WEB_WIDGET_SMTP.DOMAIN.PLACEHOLDER')"
          :message="inputMessage('domain')"
          :message-type="inputMessageType('domain')"
          @blur="touch('domain')"
        />

        <fieldset>
          <legend class="mb-2 text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.ENCRYPTION.LABEL') }}
          </legend>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label
              class="flex cursor-pointer items-center gap-2 rounded-lg bg-n-alpha-black2 px-3 py-2.5 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak"
            >
              <input v-model="state.encryption" type="radio" value="starttls" />
              {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.ENCRYPTION.STARTTLS') }}
            </label>
            <label
              class="flex cursor-pointer items-center gap-2 rounded-lg bg-n-alpha-black2 px-3 py-2.5 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak"
            >
              <input v-model="state.encryption" type="radio" value="ssl_tls" />
              {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.ENCRYPTION.SSL_TLS') }}
            </label>
          </div>
        </fieldset>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.VERIFY.LABEL') }}
            <select
              v-model="state.verifyMode"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm font-normal text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
            >
              <option
                v-for="option in verifyModeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.WEB_WIDGET_SMTP.AUTH.LABEL') }}
            <select
              v-model="state.authentication"
              class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm font-normal text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
            >
              <option
                v-for="option in authenticationOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div class="flex justify-end">
        <NextButton
          type="submit"
          :label="t('INBOX_MGMT.WEB_WIDGET_SMTP.SAVE')"
          :is-loading="isUpdating"
          :disabled="isUpdating"
        />
      </div>
    </form>
  </SettingsFieldSection>
</template>
